import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Search, Menu, Bookmark, Globe, FolderOpen, ExternalLink, ArrowLeft,
    Loader2, Plus, Check, FileText, Video, Image as ImageIcon, Mic,
    BookOpen, User, Folder,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import BrainSyncLoader from '../components/BrainSyncLoader';
import VerifiedBadge from '../components/VerifiedBadge';

const API = '/api';

// Tiny content-type filter, mirroring Dashboard's chip strip.
const CONTENT_TYPES = [
    { id: null, label: 'All', icon: Bookmark },
    { id: 'video', label: 'Videos', icon: Video },
    { id: 'article', label: 'Articles', icon: FileText },
    { id: 'image', label: 'Images', icon: ImageIcon },
    { id: 'audio', label: 'Audio', icon: Mic },
    { id: 'recipe', label: 'Recipes', icon: BookOpen },
];

const getContentIcon = (type) => {
    const found = CONTENT_TYPES.find((t) => t.id === type);
    const IconCmp = found?.icon || Bookmark;
    return <IconCmp className="w-3.5 h-3.5 text-primary" />;
};

// PublicProfilePage - `/{username}`. Read-only Dashboard mirror filtered to
// the profile owner's `share_visibility=public` bookmarks + categories.
// Logged-in visitors get a "Save to my brain" CTA on each card (clones
// into their own library via the existing `/api/share/{id}/clone`
// endpoint).
const PublicProfilePage = () => {
    const { username } = useParams();
    const navigate = useNavigate();
    const { user: viewer } = useAuth();

    const [profile, setProfile] = useState(null);
    const [categories, setCategories] = useState([]);
    const [bookmarks, setBookmarks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [contentType, setContentType] = useState(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [cloningId, setCloningId] = useState(null);
    const [clonedIds, setClonedIds] = useState(() => new Set());

    const fetchLockRef = useRef(0);

    // Dismiss the pre-React HTML splash once we're mounted so the profile
    // (or loader / 404) becomes visible. Without this the splash stays on
    // top with z-index 9999 and we'd appear to "hang" on the brain icon.
    useEffect(() => {
        window.__brainsyncDismissSplash?.();
    }, []);

    // SEO - dynamically set <title>, meta description, and OG/Twitter tags
    // to include the owner's real display name. Without this, Google
    // indexes every public profile under BrainSync's generic homepage
    // title and crawl results never surface the owner. We update existing
    // tags by name/property where available, and create them if missing,
    // because Google's JS-enabled crawl reads the final DOM state.
    useEffect(() => {
        if (!profile) return;
        const fullName = profile.name || profile.first_name || profile.username;
        const title = `${fullName} (@${profile.username}) · BrainSync`;
        const desc = profile.bookmark_count > 0
            ? `${fullName}'s public collection on BrainSync - ${profile.bookmark_count} curated article${profile.bookmark_count === 1 ? '' : 's'}, video${profile.bookmark_count === 1 ? '' : 's'}, and notes.`
            : `${fullName} on BrainSync - AI-powered bookmarks and notes.`;
        const url = `https://brainsync.pro/${profile.username}`;
        // OG card endpoint - backend renders a 1200×630 PNG with the
        // owner's avatar, real name, and public stats. The `?v=` query
        // string is the design version: bumping it server-side
        // auto-invalidates CDN + social-preview caches for old links.
        // Match the version we expose on the modal preview too so all
        // surfaces refresh in lockstep.
        const cardVersion = profile.card_version || 'v3';
        const image = `https://brainsync.pro/api/og/profile/${profile.username}.png?v=${cardVersion}`;

        document.title = title;

        const setMeta = (selector, attr, content) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement('meta');
                const [a, v] = selector.replace(/^meta\[|\]$/g, '').split('=');
                el.setAttribute(a, v.replace(/['"]/g, ''));
                document.head.appendChild(el);
            }
            el.setAttribute(attr, content);
        };

        setMeta('meta[name="description"]', 'content', desc);
        setMeta('meta[property="og:title"]', 'content', title);
        setMeta('meta[property="og:description"]', 'content', desc);
        setMeta('meta[property="og:url"]', 'content', url);
        setMeta('meta[property="og:image"]', 'content', image);
        setMeta('meta[property="og:type"]', 'content', 'profile');
        setMeta('meta[property="profile:username"]', 'content', profile.username);
        if (profile.first_name) {
            setMeta('meta[property="profile:first_name"]', 'content', profile.first_name);
        }
        const lastName = (profile.name || '').split(' ').slice(1).join(' ');
        if (lastName) {
            setMeta('meta[property="profile:last_name"]', 'content', lastName);
        }
        setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
        setMeta('meta[name="twitter:title"]', 'content', title);
        setMeta('meta[name="twitter:description"]', 'content', desc);
        setMeta('meta[name="twitter:image"]', 'content', image);

        // Canonical link tag - tells Google the authoritative URL is
        // /{username} (no trailing slash) regardless of how they arrived.
        let canonical = document.head.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', url);

        // JSON-LD structured data for ProfilePage - explicit name → username
        // mapping that Google indexes for entity recognition. Cleaned up
        // on unmount so other pages don't inherit our schema.
        const ldId = 'public-profile-jsonld';
        // Drop the inline injector's early ProfilePage (emitted in
        // index.html for the edge prerender) so we don't ship two.
        document.querySelectorAll('script[data-bs-entity-ld="profile"], script[data-bs-breadcrumb]')
            .forEach((el) => el.remove());
        let ld = document.getElementById(ldId);
        if (!ld) {
            ld = document.createElement('script');
            ld.type = 'application/ld+json';
            ld.id = ldId;
            document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ProfilePage',
            url,
            mainEntity: {
                '@type': 'Person',
                name: fullName,
                alternateName: profile.username,
                identifier: profile.username,
                image: profile.picture || undefined,
                url,
            },
        });

        // BreadcrumbList - Home > @username. Renders a breadcrumb trail
        // above the URL in Google's SERP card. Separate <script> tag so
        // each schema validates independently in rich-results testing.
        const crumbId = 'public-profile-crumb-jsonld';
        let crumb = document.getElementById(crumbId);
        if (!crumb) {
            crumb = document.createElement('script');
            crumb.type = 'application/ld+json';
            crumb.id = crumbId;
            document.head.appendChild(crumb);
        }
        crumb.textContent = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://brainsync.pro/' },
                { '@type': 'ListItem', position: 2, name: `@${profile.username}`, item: url },
            ],
        });

        return () => {
            // Restore the default homepage title when navigating away so
            // it doesn't bleed into Dashboard / Landing.
            document.title = 'BrainSync · Bookmarks that remember themselves';
            const stale = document.getElementById(ldId);
            if (stale) stale.remove();
            const staleCrumb = document.getElementById(crumbId);
            if (staleCrumb) staleCrumb.remove();
        };
    }, [profile]);

    // Debounce search input.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    // Fetch the profile header + sidebar categories on username change.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const profRes = await fetch(`${API}/public/profiles/${encodeURIComponent(username)}`);
                if (!profRes.ok) {
                    setError(profRes.status === 404 ? 'not_found' : 'fetch_failed');
                    setLoading(false);
                    return;
                }
                const profData = await profRes.json();
                if (cancelled) return;
                setProfile(profData);

                const catRes = await fetch(`${API}/public/profiles/${encodeURIComponent(username)}/categories`);
                if (catRes.ok && !cancelled) {
                    setCategories(await catRes.json());
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Profile fetch error:', err);
                    setError('fetch_failed');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [username]);

    // Fetch bookmarks (resets on filter change, paginates on loadMore).
    const fetchBookmarks = useCallback(async ({ loadMore = false } = {}) => {
        if (!username) return;
        const token = ++fetchLockRef.current;
        if (loadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        try {
            const params = new URLSearchParams();
            if (debouncedSearch) params.append('search', debouncedSearch);
            if (contentType) params.append('content_type', contentType);
            if (selectedCategoryId) params.append('category_id', selectedCategoryId);
            params.append('limit', '20');
            if (loadMore && nextCursor) params.append('cursor', nextCursor);
            const res = await fetch(`${API}/public/profiles/${encodeURIComponent(username)}/bookmarks?${params.toString()}`);
            if (token !== fetchLockRef.current) return; // stale
            if (!res.ok) return;
            const data = await res.json();
            if (token !== fetchLockRef.current) return;
            if (loadMore) {
                setBookmarks((prev) => [...prev, ...(data.bookmarks || [])]);
            } else {
                setBookmarks(data.bookmarks || []);
            }
            setHasMore(!!data.has_more);
            setNextCursor(data.next_cursor || null);
        } catch (err) {
            if (token === fetchLockRef.current) console.error('Public bookmarks fetch error:', err);
        } finally {
            if (token === fetchLockRef.current) {
                setLoading(false);
                setLoadingMore(false);
            }
        }
    }, [username, debouncedSearch, contentType, selectedCategoryId, nextCursor]);

    // Re-run bookmark fetch when filters change.
    useEffect(() => {
        if (!profile) return;
        setNextCursor(null);
        fetchBookmarks({ loadMore: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.username, debouncedSearch, contentType, selectedCategoryId]);

    const isOwner = viewer && profile && viewer.user_id === profile.user_id;

    const handleClone = async (bookmark, e) => {
        e?.stopPropagation();
        if (!viewer) {
            // Stash the source so post-login the AuthCallback flow can
            // finish the save automatically - the user doesn't lose the
            // bookmark they wanted just because we bounced them to sign-in.
            try {
                sessionStorage.setItem('bs_clone_after_login', JSON.stringify({
                    bookmark_id: bookmark.bookmark_id,
                    username: profile?.username || username,
                    ts: Date.now(),
                }));
            } catch (_) {}
            toast.info('Sign in to save this to your BrainSync');
            navigate('/');
            return;
        }
        if (isOwner) {
            navigate(`/bookmark/${bookmark.bookmark_id}`);
            return;
        }
        if (cloningId === bookmark.bookmark_id || clonedIds.has(bookmark.bookmark_id)) return;
        setCloningId(bookmark.bookmark_id);
        try {
            const res = await fetch(`${API}/share/${bookmark.bookmark_id}/clone`, {
                method: 'POST',
                credentials: 'include',
            });
            if (!res.ok) {
                toast.error('Could not save this bookmark');
                return;
            }
            setClonedIds((prev) => {
                const next = new Set(prev);
                next.add(bookmark.bookmark_id);
                return next;
            });
            const data = await res.json();
            if (data.already_cloned) {
                toast.info('Already in your BrainSync');
            } else {
                toast.success('Saved to your BrainSync', {
                    action: { label: 'View', onClick: () => navigate(`/bookmark/${data.bookmark_id}`) },
                });
            }
        } catch (err) {
            toast.error('Could not save this bookmark');
        } finally {
            setCloningId(null);
        }
    };

    // Show every category the owner marked Public - including subcategories.
    // The owner may publish a sub like "Bangkok" without publishing its
    // parent ("Travel"); we'd hide it if we only listed parents. We do
    // sort parents first then subs for visual grouping.
    const visibleCategories = useMemo(() => {
        const parents = categories.filter((c) => !c.parent_id);
        const subs = categories.filter((c) => c.parent_id);
        return [...parents, ...subs];
    }, [categories]);

    const sidebarBody = (
        <div className="space-y-1">
            <button
                onClick={() => { setSelectedCategoryId(null); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                    !selectedCategoryId ? 'bg-primary text-primary-foreground shadow-md' : 'hover:bg-secondary'
                }`}
                data-testid="public-all-bookmarks-btn"
            >
                <Bookmark className="w-4 h-4" />
                <span className="flex-1 font-medium text-sm">All public bookmarks</span>
                <span className="text-xs opacity-70">{profile?.bookmark_count ?? '-'}</span>
            </button>
            {visibleCategories.length > 0 && (
                <div className="pt-3 mt-3 border-t border-border/40">
                    <h3 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2 px-3">
                        Public collections
                    </h3>
                    {visibleCategories.map((cat) => (
                        <button
                            key={cat.category_id}
                            onClick={() => { setSelectedCategoryId(cat.category_id); setMobileMenuOpen(false); }}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                                selectedCategoryId === cat.category_id ? 'bg-primary text-primary-foreground shadow-md' : 'hover:bg-secondary'
                            } ${cat.parent_id ? 'pl-7' : ''}`}
                            style={selectedCategoryId !== cat.category_id ? { borderLeft: `3px solid ${cat.color || '#10b981'}` } : {}}
                            data-testid={`public-category-${cat.category_id}`}
                        >
                            <Folder className="w-4 h-4" style={{ color: selectedCategoryId === cat.category_id ? undefined : cat.color }} />
                            <span className="flex-1 font-medium text-sm truncate">{cat.name}</span>
                            <span className="text-xs opacity-70">{cat.bookmark_count || 0}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    if (error === 'not_found') {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-6" data-testid="profile-not-found">
                <div className="text-center max-w-sm">
                    <div className="w-16 h-16 rounded-2xl bg-secondary mx-auto mb-4 flex items-center justify-center">
                        <User className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h1 className="font-heading text-xl font-semibold mb-2">Profile not found</h1>
                    <p className="text-muted-foreground text-sm mb-6">
                        <span className="font-mono text-foreground">brainsync.pro/{username}</span> doesn't belong to anyone yet.
                    </p>
                    <Button onClick={() => navigate('/')} variant="outline" className="rounded-full">
                        Go home
                    </Button>
                </div>
            </div>
        );
    }

    if (!profile && loading) {
        return (
            <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center" data-testid="profile-loading">
                <BrainSyncLoader />
            </div>
        );
    }

    if (!profile) return null;

    const firstName = profile.first_name || profile.username;

    return (
        <div className="min-h-screen bg-background" data-testid="public-profile-page">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-lg border-b border-border">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="sm" className="lg:hidden h-9 w-9 p-0" data-testid="public-mobile-menu-btn">
                                    <Menu className="w-5 h-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="w-72 p-4 flex flex-col">
                                <SheetHeader className="mb-6 flex-shrink-0">
                                    <SheetTitle className="flex items-center gap-2">
                                        {profile.picture ? (
                                            <img src={profile.picture} alt={firstName} className="w-8 h-8 rounded-full object-cover" />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                                                <User className="w-4 h-4 text-white" />
                                            </div>
                                        )}
                                        <span className="font-heading font-bold truncate">{firstName}'s BrainSync</span>
                                    </SheetTitle>
                                </SheetHeader>
                                <div className="flex-1 overflow-y-auto -mx-1 px-1">
                                    {sidebarBody}
                                </div>
                            </SheetContent>
                        </Sheet>

                        <button
                            type="button"
                            onClick={() => navigate(viewer ? '/dashboard' : '/')}
                            className="flex items-center gap-2 cursor-pointer bg-transparent border-0 p-0 min-w-0"
                            data-testid="public-back-btn"
                        >
                            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium text-muted-foreground hidden sm:inline">
                                {viewer ? 'Dashboard' : 'BrainSync'}
                            </span>
                        </button>
                    </div>

                    {!viewer && (
                        <Button
                            onClick={() => navigate('/')}
                            className="rounded-full px-4 h-9 text-sm font-semibold bg-primary"
                            data-testid="public-signup-btn"
                        >
                            Get BrainSync free
                        </Button>
                    )}
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
                {/* Profile hero */}
                <div className="flex items-start gap-4 mb-6 sm:mb-8">
                    {profile.picture ? (
                        <img
                            src={profile.picture}
                            alt={firstName}
                            className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover ring-2 ring-white shadow-md flex-shrink-0"
                            data-testid="public-profile-picture"
                        />
                    ) : (
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-primary flex items-center justify-center ring-2 ring-white shadow-md flex-shrink-0">
                            <User className="w-7 h-7 text-white" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h1
                            className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1F2D20] truncate"
                            data-testid="public-profile-title"
                        >
                            {firstName}'s BrainSync
                            {profile.is_verified && (
                                <VerifiedBadge size="lg" className="ml-2" />
                            )}
                        </h1>
                        {/* Full-name + handle subline - gives visitors a
                            clearer sense of WHOSE collection they're
                            browsing (the H1 only shows first name). The
                            handle doubles as a visual nod that the
                            page is at a canonical username URL.
                            Hidden when the owner has no separate
                            display name (shadow accounts created by
                            shared-link flows). */}
                        {(profile.name || profile.username) && (
                            <p
                                className="mt-1 text-sm sm:text-base text-muted-foreground truncate"
                                data-testid="public-profile-handle-subline"
                            >
                                {profile.name && (
                                    <span className="font-medium text-foreground/80">{profile.name}</span>
                                )}
                                {profile.is_verified && (
                                    <VerifiedBadge size="sm" className="ml-1" />
                                )}
                                {profile.name && profile.username && (
                                    <span className="mx-2 text-muted-foreground/50">·</span>
                                )}
                                {profile.username && (
                                    <span className="font-mono">@{profile.username}</span>
                                )}
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex gap-6">
                    {/* Desktop sidebar */}
                    <aside className="hidden lg:block w-60 flex-shrink-0">
                        <div className="sticky top-24">
                            {sidebarBody}
                        </div>
                    </aside>

                    <main className="flex-1 min-w-0">
                        {/* Search bar */}
                        <div className="relative mb-4">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={`Search ${firstName}'s public brain…`}
                                className="pl-9 h-11 rounded-xl"
                                data-testid="public-search-input"
                            />
                        </div>

                        {/* Public categories strip - mirrors Dashboard's
                            chip bar so the public-profile recipient gets
                            the same browse-by-topic affordance the owner
                            uses internally. On desktop the sidebar
                            already shows categories with counts, but on
                            mobile the sidebar is hidden behind a drawer
                            - without this strip there was no quick way
                            to filter by topic on a phone. */}
                        {visibleCategories.length > 0 && (
                            <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1 scrollbar-none" data-testid="public-categories-bar">
                                <button
                                    onClick={() => setSelectedCategoryId(null)}
                                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                                        !selectedCategoryId
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                                    }`}
                                    data-testid="public-category-chip-all"
                                >
                                    <Bookmark className="w-3 h-3" />
                                    All
                                    {profile?.bookmark_count > 0 && (
                                        <span className={`text-[10px] ${!selectedCategoryId ? 'opacity-70' : 'text-muted-foreground/60'}`}>
                                            {profile.bookmark_count}
                                        </span>
                                    )}
                                </button>
                                {visibleCategories.map((cat) => {
                                    const isActive = selectedCategoryId === cat.category_id;
                                    return (
                                        <button
                                            key={cat.category_id}
                                            onClick={() => setSelectedCategoryId(isActive ? null : cat.category_id)}
                                            className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                                                isActive ? 'text-white shadow-sm' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                                            }`}
                                            style={isActive ? { backgroundColor: cat.color || '#10b981' } : {}}
                                            data-testid={`public-category-chip-${cat.category_id}`}
                                        >
                                            <Folder className="w-3 h-3" style={isActive ? {} : { color: cat.color }} />
                                            {cat.name}
                                            {cat.bookmark_count > 0 && (
                                                <span className={`text-[10px] ${isActive ? 'opacity-70' : 'text-muted-foreground/60'}`}>
                                                    {cat.bookmark_count}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Bookmarks grid */}
                        {loading ? (
                            <div className="flex justify-center py-16">
                                <BrainSyncLoader />
                            </div>
                        ) : bookmarks.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="w-16 h-16 rounded-2xl bg-secondary mx-auto mb-4 flex items-center justify-center">
                                    <Bookmark className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <h3 className="font-heading text-lg font-semibold mb-2">
                                    {debouncedSearch || contentType || selectedCategoryId
                                        ? 'Nothing matches that filter'
                                        : `${firstName} hasn't shared any bookmarks yet`}
                                </h3>
                                {(debouncedSearch || contentType || selectedCategoryId) && (
                                    <Button
                                        variant="ghost"
                                        onClick={() => {
                                            setSearch(''); setContentType(null); setSelectedCategoryId(null);
                                        }}
                                        className="rounded-full"
                                    >
                                        Clear filters
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                                {(() => {
                                    // When the visitor is drilling into a single
                                    // public collection that the curator marked
                                    // "Show numbering", emit a #N rank badge over
                                    // each card - mirrors the standalone /c/{id}
                                    // collection page so a "Top 15" list looks
                                    // the same whether it's viewed via the
                                    // profile sidebar or the share link.
                                    const selectedCat = selectedCategoryId
                                        ? categories.find((c) => c.category_id === selectedCategoryId)
                                        : null;
                                    const showRankBadges = !!(selectedCat && selectedCat.show_numbering);
                                    return bookmarks.map((bookmark, idx) => {
                                    const isCloned = clonedIds.has(bookmark.bookmark_id);
                                    const isCloning = cloningId === bookmark.bookmark_id;
                                    return (
                                        <div key={bookmark.bookmark_id} className="relative">
                                            {showRankBadges && (
                                                <span
                                                    className="absolute -top-2 -left-2 z-10 min-w-[32px] h-8 px-2 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white text-sm font-bold tabular-nums shadow-md ring-2 ring-white pointer-events-none"
                                                    data-testid={`public-profile-rank-badge-${bookmark.bookmark_id}`}
                                                >
                                                    #{idx + 1}
                                                </span>
                                            )}
                                        <div
                                            className="bg-white border border-border/80 rounded-2xl overflow-hidden transition-shadow hover:shadow-lg group cursor-pointer shadow-card flex flex-col"
                                            onClick={() => {
                                                // Owner → in-app detail view (full edit access).
                                                // Everyone else → the public share page, which
                                                // works for both authenticated visitors and
                                                // anonymous viewers (previously sent everyone
                                                // to /bookmark/{id} which is auth-gated and
                                                // bounced them to the landing page with no
                                                // context - see bug report #4).
                                                if (isOwner) {
                                                    navigate(`/bookmark/${bookmark.bookmark_id}`);
                                                } else {
                                                    navigate(`/share/${bookmark.bookmark_id}`);
                                                }
                                            }}
                                            data-testid={`public-bookmark-card-${bookmark.bookmark_id}`}
                                        >
                                            {bookmark.thumbnail && (
                                                <div className="aspect-video relative overflow-hidden bg-secondary">
                                                    <img
                                                        src={bookmark.thumbnail}
                                                        alt={bookmark.title}
                                                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                                                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                                                    />
                                                    <div className="absolute top-2 left-2">
                                                        <div className="w-7 h-7 rounded-lg bg-white/90 backdrop-blur flex items-center justify-center shadow-sm">
                                                            {getContentIcon(bookmark.content_type)}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="p-3 sm:p-4 flex-1 flex flex-col">
                                                <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors mb-1.5">
                                                    {bookmark.title}
                                                </h3>
                                                {bookmark.summary && (
                                                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3 leading-relaxed">
                                                        {bookmark.summary.replace(/^[A-Z\s]+:?\s*/, '').slice(0, 200)}
                                                    </p>
                                                )}
                                                {bookmark.tags && bookmark.tags.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 mb-3">
                                                        {bookmark.tags.slice(0, 3).map((t) => (
                                                            <span key={t} className="text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-secondary text-muted-foreground">
                                                                #{t}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className="flex items-center justify-between gap-2 mt-auto pt-2 border-t border-border/40">
                                                    <a
                                                        href={bookmark.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                                                        data-testid={`public-bookmark-source-${bookmark.bookmark_id}`}
                                                    >
                                                        <ExternalLink className="w-3 h-3" />
                                                        Source
                                                    </a>
                                                    {!isOwner && (
                                                        <button
                                                            onClick={(e) => handleClone(bookmark, e)}
                                                            disabled={isCloning}
                                                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                                                                isCloned
                                                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                                    : 'bg-primary text-primary-foreground hover:opacity-90'
                                                            }`}
                                                            data-testid={`public-bookmark-clone-${bookmark.bookmark_id}`}
                                                        >
                                                            {isCloning ? (
                                                                <Loader2 className="w-3 h-3 animate-spin" />
                                                            ) : isCloned ? (
                                                                <Check className="w-3 h-3" />
                                                            ) : (
                                                                <Plus className="w-3 h-3" />
                                                            )}
                                                            {isCloned ? 'Saved' : 'Save to my BrainSync'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        </div>
                                    );
                                });
                                })()}
                            </div>
                        )}

                        {hasMore && (
                            <div className="flex justify-center mt-6">
                                <Button
                                    variant="outline"
                                    onClick={() => fetchBookmarks({ loadMore: true })}
                                    disabled={loadingMore}
                                    className="rounded-full"
                                    data-testid="public-load-more-btn"
                                >
                                    {loadingMore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                    Load more
                                </Button>
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default PublicProfilePage;
